import { useCallback, useEffect, useState } from 'react'
import { Icon } from './Icon'
import { api, type Suggestion, type SuggestionAction, type SuggestionState } from '../api'
import { addsSomething, basisOf } from '../suggestions'
import { useDialog } from '../useDialog'

/**
 * The suggestions inbox.
 *
 * This is the product. Everything else here is a competent Markdown editor; the
 * reason to use Staleless is that it tells you which page went stale and why.
 * Until now it had no interface at all — findings were produced, stored, and
 * only ever read by a scan script.
 *
 * Two rules govern the design, both from the inbox's own doc comment, because
 * there are exactly two ways to lose a reader's trust here.
 *
 * DON'T RE-ASK. Only `open` is shown by default. The history is available and
 * never volunteered.
 *
 * SAY WHY. Every item can show its evidence, and the evidence is the analyser's
 * own payload rather than a restatement of the title. A suggestion a writer
 * cannot check is a suggestion they must either obey or ignore, and they will
 * choose ignore.
 *
 * The four dismissals are not decoration either. "This is wrong" and "not now"
 * are different answers, and an inbox with one dismiss button either nags or
 * forgets.
 */
interface Props {
  open: boolean
  onClose: () => void
  /** Opens a page in the editor, so a finding leads somewhere. */
  onOpenPage: (path: string) => void
  /** Told when counts change, so the trigger's badge stays true. */
  onCounts: (counts: Record<string, number>) => void
  /**
   * The door out of an empty inbox: Pulse, which is where the assessment
   * behind these suggestions lives. An empty worklist is good news and still
   * needs somewhere to go — it used to be a sentence and a closed door.
   */
  onPulse: () => void
  /**
   * The draft being worked on, and whether this deployment can draft at all.
   *
   * §35's drafting writes to a DRAFT, never to the live site, so without one
   * there is nothing to save into and the button is not offered. `canDraft` is
   * the project's own switch — the same one that lets a writer ask for help with
   * a sentence, because it is the same provider seeing the same content.
   */
  branch: string | null
  canDraft: boolean
  /** Save the drafted page. The only way anything is written. */
  onSaveDraft: (path: string, content: string) => Promise<void>
}

/** Severity, in the semantic colours — never the Pulse ones. */
const SEVERITY: Record<string, { label: string; badge: string }> = {
  breaking: { label: 'Breaking', badge: 'badge--danger' },
  'potentially-breaking': { label: 'May break', badge: 'badge--warn' },
  additive: { label: 'Added', badge: 'badge--accent' },
  info: { label: 'Info', badge: 'badge--quiet' },
}

const TRIGGER: Record<string, string> = {
  'api-change': 'API changed',
  'undocumented-api': 'Undocumented',
  'stale-page': 'Possibly stale',
  'review-overdue': 'Review due',
  'broken-link': 'Broken link',
  'orphan-page': 'Not in navigation',
  // Somebody described what they wanted and search had nothing. Named from
  // the READER's side, because that is whose problem it is.
  'failed-search': 'Readers found nothing',
  // Somebody followed an address and got nothing. Named for what HAPPENED
  // rather than for the fix, because there are two fixes and the item's own
  // sentence is where they are offered.
  'missed-path': 'Not found',
  // A page readers read and left unhappy. The third of the sibling signals,
  // and the only one about a page that exists.
  'reader-verdict': 'Readers unhappy',
  // Shipped without a label, so the row showed the raw slug.
  'external-link': 'Link out is dead',
  // A whole question the documentation could not answer — the most direct of
  // the reader signals, named from the reader's side like the rest.
  'unanswered-question': 'Nobody answered',
  // And one it answered badly, which is a page that exists and misleads.
  'rejected-answer': 'Answer rejected',
  // The only signal from outside the documentation site: somebody gave up on
  // searching, asking and reading, and wrote to a human instead.
  'support-gap': 'Support keeps asking',
  // A link the writer made themselves, and the only trigger built on one.
  'issue-shipped': 'The issue shipped',
}

/** The three views. Anything dismissed is reachable, and never in the way. */
const VIEWS: { id: string; label: string; states: SuggestionState[] }[] = [
  { id: 'open', label: 'Needs attention', states: ['open'] },
  { id: 'later', label: 'Later', states: ['snoozed'] },
  { id: 'done', label: 'Settled', states: ['applied', 'ignored', 'not_relevant', 'resolved'] },
]

const ACTIONS: { action: SuggestionAction; label: string; title: string }[] = [
  { action: 'apply', label: 'Done', title: 'The work is done — stop reporting it' },
  { action: 'snooze', label: 'Later', title: 'Ask me again in a week' },
  { action: 'not-relevant', label: 'Not relevant', title: 'This finding does not apply here' },
  { action: 'ignore', label: 'Ignore', title: 'Never mention this again' },
]

const AGO = (iso: string): string => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.round(days / 30)
  return months === 1 ? 'a month ago' : `${months} months ago`
}

export function Inbox({
  open,
  onClose,
  onOpenPage,
  onCounts,
  onPulse,
  branch,
  canDraft,
  onSaveDraft,
}: Props) {
  const panel = useDialog<HTMLElement>(open, onClose)
  const [view, setView] = useState('open')
  const [items, setItems] = useState<Suggestion[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [acting, setActing] = useState<string | null>(null)
  const [shown, setShown] = useState<Set<string>>(new Set())
  /**
   * The drafted revision on screen, by suggestion id.
   *
   * One at a time and never applied on its own: the writer reads it beside what
   * the page says now, edits it if they like, and saves it into the draft — the
   * same rule §22's writing help follows, for the same reason.
   */
  const [drafting, setDrafting] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ id: string; path: string; text: string } | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)

  const askForDraft = async (id: string, path: string) => {
    setDrafting(id)
    setDraftError(null)
    try {
      const said = await api.draftFromFinding(id, path, branch ?? undefined)
      setDraft({ id, path, text: said.after })
      if (said.unchanged) setDraftError('It had nothing to change on that page.')
    } catch (e) {
      setDraft(null)
      setDraftError(e instanceof Error ? e.message : String(e))
    } finally {
      setDrafting(null)
    }
  }

  const load = useCallback(async (which: string) => {
    const states = VIEWS.find((v) => v.id === which)?.states ?? ['open']
    try {
      const body = await api.inbox(states)
      setItems(body.suggestions)
      setError(null)
      onCounts(body.counts)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
    // onCounts is stable in practice; including it re-runs the load on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (open) void load(view)
  }, [open, view, load])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const act = async (item: Suggestion, action: SuggestionAction) => {
    setActing(item.id)
    try {
      await api.actOnSuggestion(item.id, action)
      // Drop it from the list rather than reloading: a list that reflows under
      // the pointer makes the next click a different button than the one aimed
      // at. The counts still come from the server.
      setItems((prev) => (prev ?? []).filter((s) => s.id !== item.id))
      const body = await api.inbox(VIEWS.find((v) => v.id === view)?.states ?? ['open'])
      onCounts(body.counts)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setActing(null)
    }
  }

  if (!open) return null

  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Suggestions inbox"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">Inbox</h2>
            {/* What is under it, said before somebody scrolls: every item
                carries the evidence for the claim and one thing to do about
                it. The half-sentence promised neither. */}
            <p className="drawer__sub">
              What Pulse thinks needs a look — with the evidence, and one action each.
            </p>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="drawer__body">
          <div className="segmented inbox__views" role="tablist">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                role="tab"
                aria-selected={view === v.id}
                className={`segmented__btn${view === v.id ? ' is-active' : ''}`}
                onClick={() => {
                  setItems(null)
                  setView(v.id)
                }}
              >
                {v.label}
              </button>
            ))}
          </div>

          {error && <p className="panel__error">{error}</p>}

          {items === null && !error && <p className="drawer__hint">Reading the inbox…</p>}

          {items?.length === 0 && (
            <div className="drawer__none">
              <p className="nothing">
                {view === 'open'
                  ? 'Nothing needs attention. This is the normal state, not an empty one.'
                  : `Nothing is ${view === 'later' ? 'put off' : 'settled'} here.`}
              </p>
              {/* One act each. An empty worklist sends you to the assessment
                  that fills it; a filtered view sends you back to the work. */}
              {view === 'open' ? (
                <button
                  className="btn btn--tinted btn--sm"
                  onClick={() => {
                    onClose()
                    onPulse()
                  }}
                >
                  <Icon name="pulse" size={13} />
                  See how the pages are doing
                </button>
              ) : (
                <button
                  className="btn btn--tinted btn--sm"
                  onClick={() => {
                    setItems(null)
                    setView('open')
                  }}
                >
                  <Icon name="alert" size={13} />
                  Show what needs attention
                </button>
              )}
            </div>
          )}

          {/*
            The two words the badges use, said once.

            "evidence" and "suspicion" is the most consequential distinction on
            this panel — one says a page describes something that has actually
            changed, the other says only that it is old — and both meanings
            were held in a `title` on a `<span>`. Nothing focuses a span, so
            the sentences were reachable by hovering and by no other means, on
            the panel whose entire job is telling somebody how much to believe
            it. Pulse explains its three states this way, in a paragraph beside
            the list; this is the same sentence in the same voice.
          */}
          {(items ?? []).some((item) => basisOf(item)) && (
            <p className="drawer__hint inbox__basis-note">
              <b>Evidence</b> means something a page describes has actually changed.{' '}
              <b>Suspicion</b> means nothing proves the page is wrong — it is old, which is not the
              same thing.
            </p>
          )}

          <ul className="inbox">
            {(items ?? []).map((item) => {
              const severity = SEVERITY[item.severity] ?? SEVERITY['info']!
              const basis = basisOf(item)
              const isOpen = shown.has(item.id)
              return (
                <li key={item.id} className="inbox__item">
                  <div className="inbox__top">
                    <span className={`badge ${severity.badge}`}>{severity.label}</span>
                    <span className="inbox__trigger">{TRIGGER[item.trigger] ?? item.trigger}</span>
                    {basis && (
                      <span className={`inbox__basis inbox__basis--${basis}`}>{basis}</span>
                    )}
                    {item.confidence && item.confidence !== 'high' && (
                      /* Said out loud. A medium-confidence match presented as a
                         fact is how a tool earns a reputation for crying wolf. */
                      <span className="badge badge--quiet">{item.confidence} confidence</span>
                    )}
                    <span className="inbox__age">first seen {AGO(item.firstSeenAt)}</span>
                  </div>

                  <h3 className="inbox__title">{item.title}</h3>
                  <p className="inbox__detail">{item.detail}</p>

                  {item.pages.length > 0 && (
                    <div className="inbox__pages">
                      {item.pages.map((p, i) =>
                        p.path ? (
                          <button
                            key={`${p.path}-${i}`}
                            className="inbox__page"
                            onClick={() => {
                              onOpenPage(p.path!)
                              onClose()
                            }}
                            data-tip={`Open ${p.path}`}
                          >
                            <Icon name="page" size={12} />
                            <span className="truncate">
                              {p.path.replace(/^pages\//, '').replace(/\.md$/, '')}
                            </span>
                          </button>
                        ) : null,
                      )}
                    </div>
                  )}

                  {/*
                    §35: draft the change this finding asks for.
                    Offered only on an item that names a page — there is nothing
                    to revise otherwise — and only where the project turned help
                    with the words on. It writes to the DRAFT, so the button says
                    which draft, and nothing reaches the live site.
                  */}
                  {canDraft && branch && item.pages.some((p) => p.path) && (
                    <div className="inbox__draft">
                      {draft?.id === item.id ? (
                        <>
                          <p className="inbox__draft-lead">
                            The page as it could be. Edit it if you like, then save it into{' '}
                            <code className="mono">{branch}</code>, or throw it away.
                          </p>
                          <textarea
                            className="inbox__draft-text"
                            data-inbox-draft
                            rows={14}
                            value={draft.text}
                            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                          />
                          <div className="inbox__draft-row">
                            <button
                              className="btn btn--primary btn--sm"
                              data-inbox-draft-save
                              onClick={() => {
                                void onSaveDraft(draft.path, draft.text)
                                  .then(() => {
                                    setDraft(null)
                                    setDraftError(null)
                                  })
                                  .catch((e: unknown) =>
                                    setDraftError(e instanceof Error ? e.message : String(e)),
                                  )
                              }}
                            >
                              Save it to this draft
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              data-inbox-draft-discard
                              onClick={() => {
                                setDraft(null)
                                setDraftError(null)
                              }}
                            >
                              Discard
                            </button>
                          </div>
                        </>
                      ) : (
                        <button
                          className="btn btn--tinted btn--sm"
                          data-inbox-draft-ask
                          disabled={drafting !== null}
                          onClick={() => {
                            const path = item.pages.find((p) => p.path)?.path
                            if (path) void askForDraft(item.id, path)
                          }}
                        >
                          {drafting === item.id ? 'Drafting…' : 'Draft the change'}
                        </button>
                      )}
                      {/*
                        One error, on the row it belongs to. There is one draft
                        at a time, so an error is either about this row's draft
                        or about the ask that failed on this row — and it was
                        two identical branches saying so.
                      */}
                      {draftError && (draft?.id === item.id || drafting === null) && (
                        <p className="panel__error" role="alert">
                          {draftError}
                        </p>
                      )}
                    </div>
                  )}

                  {addsSomething(item) && (
                    <>
                      <button
                        className="inbox__why"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setShown((prev) => {
                            const next = new Set(prev)
                            if (next.has(item.id)) next.delete(item.id)
                            else next.add(item.id)
                            return next
                          })
                        }
                      >
                        <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={12} />
                        Why this is here
                      </button>
                      {isOpen && (
                        <ul className="inbox__evidence">
                          {item.evidence.map((e, i) => (
                            <li key={i}>
                              <code>{e.rule ?? 'evidence'}</code>
                              {e.matched && <span className="mono"> {e.matched}</span>}
                              {typeof e.line === 'number' && (
                                <span className="inbox__line"> line {e.line}</span>
                              )}
                              {e.detail && <span> — {e.detail}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}

                  <div className="inbox__actions">
                    {item.state === 'open' ? (
                      ACTIONS.map((a) => (
                        <button
                          key={a.action}
                          className={`btn btn--sm ${a.action === 'apply' ? 'btn--tinted' : 'btn--ghost'}`}
                          data-tip={a.title}
                          disabled={acting === item.id}
                          onClick={() => void act(item, a.action)}
                        >
                          {a.label}
                        </button>
                      ))
                    ) : (
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={acting === item.id}
                        onClick={() => void act(item, 'reopen')}
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </aside>
    </div>
  )
}
